/**
 * Store Pulse alerts surface (plan D16/§4.2): the alerts page/API contract. Read-only list/counts,
 * plus the admin actions the page exposes - acknowledge, resolve, mute, unmute, clear_network_bad.
 */
import { z } from 'zod';

export const STORE_PULSE_ANOMALY_RULES = [
  'silence', 'drop', 'spike', 'slow_decline', 'conversion_break', 'missing_report', 'pipeline_missing', 'pipeline_suspect',
  'physical_coverage_gap', 'physical_invariant_failure', 'physical_legacy_contract', 'physical_source_unavailable',
] as const;

export const STORE_PULSE_ANOMALY_SEVERITIES = ['critical', 'high', 'medium'] as const;
export const STORE_PULSE_ANOMALY_STATES = ['open', 'acknowledged', 'resolved', 'muted'] as const;

/** Matches D-I's counts query, and is the alerts page's default list filter too (so the header total and the badge/chip can never drift on first load). */
export const STORE_PULSE_ALERT_ATTENTION_SEVERITIES = ['critical', 'high'] as const;
export const STORE_PULSE_ALERT_OPEN_STATES = ['open', 'acknowledged'] as const;

export const storePulseAnomalyRowSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string().uuid().nullable(),
  store_name: z.string().nullable(),
  metric: z.string(),
  rule: z.enum(STORE_PULSE_ANOMALY_RULES),
  severity: z.enum(STORE_PULSE_ANOMALY_SEVERITIES),
  slot: z.string(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  observed: z.number().nullable(),
  expected: z.number().nullable(),
  evidence: z.record(z.unknown()),
  suspected_pipeline: z.boolean(),
  state: z.enum(STORE_PULSE_ANOMALY_STATES),
  resolved_reason: z.enum(['data_corrected', 'network_bad_slot', 'rule_cleared']).nullable(),
  state_changed_by: z.string().uuid().nullable(),
  state_changed_at: z.string().nullable(),
  notified_at: z.string().nullable(),
  ai_analysis_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type StorePulseAnomalyRow = z.infer<typeof storePulseAnomalyRowSchema>;

export const storePulseAlertCountsResponseSchema = z.object({
  ok: z.literal(true),
  count: z.number().int().nonnegative(),
});

export type StorePulseAlertCounts = z.infer<typeof storePulseAlertCountsResponseSchema>;

export const storePulseAlertListResponseSchema = z.object({
  ok: z.literal(true),
  rows: z.array(storePulseAnomalyRowSchema),
  total: z.number().int().nonnegative(),
});

export type StorePulseAlertListResponse = z.infer<typeof storePulseAlertListResponseSchema>;

/** Network-bad slots and unmatched Store Pulse names, for the alerts page's own panels (read-only, plan §4.2 Surfaces). */
export const storePulseNetworkStatusResponseSchema = z.object({
  ok: z.literal(true),
  networkBadSlots: z.array(z.object({ slot: z.string(), reason: z.string().nullable() })),
  unmatchedNames: z.array(z.string()),
  lastScan: z.object({
    scannedAt: z.string().nullable(),
    networkWeek: z.string().nullable(),
    settled: z.boolean().nullable(),
    storeCount: z.number().int().nonnegative().nullable(),
    sourcesUnavailable: z.boolean(),
  }),
});

export type StorePulseNetworkStatusResponse = z.infer<typeof storePulseNetworkStatusResponseSchema>;

export const storePulseAlertDecisionSchema = z.object({
  action: z.enum(['acknowledge', 'resolve', 'mute', 'unmute', 'clear_network_bad']),
  id: z.string().uuid().optional(),
  /** Required for mute/unmute (a mute targets a rule going forward, not one historical row - it may not have an anomaly row to attach an `id` to yet; plan D-I contract note). */
  location_id: z.string().uuid().optional(),
  rule: z.enum(STORE_PULSE_ANOMALY_RULES).optional(),
  /** mute only; absent/null means indefinite. */
  until: z.string().datetime().nullable().optional(),
  /** clear_network_bad only. */
  slot: z.string().optional(),
}).strict().superRefine((decision, ctx) => {
  if ((decision.action === 'acknowledge' || decision.action === 'resolve') && !decision.id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${decision.action} requires id.`, path: ['id'] });
  }
  if ((decision.action === 'mute' || decision.action === 'unmute') && (!decision.location_id || !decision.rule)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${decision.action} requires location_id and rule.`, path: ['location_id'] });
  }
  if (decision.action === 'clear_network_bad' && !decision.slot) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'clear_network_bad requires slot.', path: ['slot'] });
  }
});

export type StorePulseAlertDecision = z.infer<typeof storePulseAlertDecisionSchema>;

export const storePulseAlertDecisionResponseSchema = z.object({
  ok: z.literal(true),
});
